import { CountriesFlagsForLanguage } from '@/components/countries';
import { ProfilePictureLight } from '@/components/profile-picture/profile-picture.light';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { useGetPersonalInfo, useGetPrivacyPolicies } from '@/hooks/api';
import { auth, formatDate, normalizeName } from '@/lib';
import { ChevronRight, ExternalLink, Pencil, SignOut } from '@nosinovacao/fe-icons';
import { ActionCard, Button, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type ReactNode } from 'react';
import { type SettingsStep, type StepNavigation } from './models';
import { env } from '@/env';

export function SettingsMainSection({ goToStep }: Readonly<StepNavigation<SettingsStep, never>>) {
  const { data } = useGetPersonalInfo();
  const { data: privacyPolicies, isLoading: isLoadingConsents } = useGetPrivacyPolicies();
  const { t, language } = useI18n();
  const hasConsents = !!privacyPolicies?.length;
  const CountryFlag = CountriesFlagsForLanguage[language] || <></>;
  return (
    <>
      <div className="mt-3 flex items-center gap-4 pb-1 pt-5">
        <ProfilePictureLight
          className="shrink-0"
          size={72}
          name={data?.DisplayName}
          src={data?.PamPictureUri}
        />
        <div className="grid gap-1 text-nos-neutral-1000 font-title-h4">
          <span className="truncate font-bold">{normalizeName(data?.DisplayName ?? '')}</span>
          {data?.BirthDate ? (
            <span className="font-light">{formatDate(data?.BirthDate)}</span>
          ) : null}
        </div>
        <button
          type="button"
          className="ml-auto shrink-0 cursor-pointer text-nos-neutral-700"
          onClick={() => goToStep?.('update-personal-info')}
          aria-label={t('Common.UpdatePersonalInfo.Title')}
          data-tid="button-edit-personal-info"
        >
          <Pencil width={24} height={24} />
        </button>
      </div>
      <div className="-mx-5 flex h-full flex-col overflow-y-auto bg-nos-neutral-75 tablet:-mx-8">
        <div className="mb-2 h-4 shrink-0 bg-nos-neutral-0 [clip-path:_polygon(100%_0,_0%_100%,_0_0)]" />
        <SkeletonLight
          loading={isLoadingConsents}
          className="mx-5 my-3 min-h-[100px] grow rounded-lg tablet:mx-5"
          count={4}
        >
          <div className="grid gap-7 px-5 tablet:px-8">
            <SettingsItem title={t('Common.UserSettings.Configurations')}>
              <li>
                <ActionCard
                  skin="light"
                  variant="quick-link"
                  preTitle={t('Common.Language.CardTitle')}
                  subTitle={<></>}
                  title={
                    <div className="flex items-center gap-2">
                      {t(`Common.Language.Options.${language}`)} <CountryFlag />
                    </div>
                  }
                  icon={<ChevronRight width={20} height={20} />}
                  className="w-full bg-nos-neutral-0"
                  data-tid="card-language"
                  onClick={() => {
                    goToStep?.('language');
                  }}
                />
              </li>
            </SettingsItem>
            <SettingsItem title={t('Common.UserSettings.Legal')}>
              {hasConsents ? (
                <li>
                  <ActionCard
                    skin="light"
                    variant="quick-link"
                    preTitle={t('Common.Consents.CardPreTitle')}
                    subTitle={<></>}
                    title={t('Common.Consents.CardTitle')}
                    icon={<ChevronRight width={20} height={20} />}
                    className="w-full bg-nos-neutral-0"
                    data-tid="card-consents"
                    onClick={() => {
                      goToStep?.('consents');
                    }}
                  />
                </li>
              ) : null}
              <li>
                <ActionCard
                  skin="light"
                  variant="quick-link"
                  preTitle={t('Common.TermsAndConditions.CardPreTitle')}
                  subTitle={<></>}
                  title={t('Common.TermsAndConditions.CardTitle')}
                  icon={<ExternalLink width={20} height={20} />}
                  className="w-full bg-nos-neutral-0"
                  data-tid="card-terms-and-conditions"
                  asChild
                >
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={env.TermsAndConditionsLink}
                    aria-label={t('Common.TermsAndConditions.CardTitle')}
                  />
                </ActionCard>
              </li>
              <li>
                <ActionCard
                  skin="light"
                  variant="quick-link"
                  preTitle={t('Common.DeleteAccount.CardPreTitle')}
                  subTitle={<></>}
                  title={t('Common.DeleteAccount.CardTitle')}
                  icon={<ChevronRight width={20} height={20} />}
                  className="w-full bg-nos-neutral-0"
                  data-tid="card-delete-account"
                  onClick={() => {
                    goToStep?.('delete-account');
                  }}
                />
              </li>
            </SettingsItem>
          </div>
        </SkeletonLight>
        <div className=" mt-auto grid h-fit place-items-center gap-2 pb-8 pt-20">
          <Button
            skin="light"
            variant="tertiary"
            onClick={() => auth.logout(false)}
            data-tid="button-logout"
          >
            <SignOut width={20} height={20} /> {t('Common.Logout')}
          </Button>
          <span className="font-light text-nos-neutral-900 font-text-p2">
            {t('Common.UserSettings.Rights')}
          </span>
        </div>
      </div>
    </>
  );
}

function SettingsItem({ children, title }: Readonly<{ title: string; children: ReactNode }>) {
  return (
    <div className="grid gap-2">
      <span className="font-light text-nos-neutral-900 font-text-p2">{title}</span>
      <ul className="grid gap-2">{children}</ul>
    </div>
  );
}
